import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGoogleMaps } from 'common/google-map-context/GoogleMapContext';
import { setRecentlyViewed } from 'store/search/search-actions';
import useConfig from 'hooks/use-config';
import googleMapWidget from './google-map-widget';
import useZIndex from './use-zindex';
import GoogleMapInfoWindow from './GoogleMapInfoWindow';
/**
 * A component that displays a Google Map with markers for spots.
 *
 * @param {SHGoogleMapProps} props - The props of the component.
 * @returns {object} The SHGoogleMap component.
 */
const SHGoogleMap = ({ isAdmin, destination, kmlMapLayerEnabled, kmlMapLayerURL, spots, searchRequestId, zoom, onGoogleMapLoaded, onMarkerClick: onSpotDetailCallback, onPan: onPanProp, onZoom, showAllSpots = false, }) => {
    const isSpotDetailOpen = useSelector((state) => Boolean(state.spots.selectedSpotId));
    const { filterId } = useSelector((state) => state.filters);
    const [prevFilterId, setPrevFilterId] = useState(filterId);
    const dispatch = useDispatch();
    const { googleMapsId } = useConfig();
    const { loaded } = useGoogleMaps();
    const { title: destinationTitle } = destination;
    const mapWidgetRef = useRef(null);
    const mapContainerRef = useRef(null);
    const [previewSpot, setPreviewSpot] = useState(null);
    const centerLatLng = useMemo(() => ({
        lat: destination.position.latitude,
        lng: destination.position.longitude,
    }), [destination.position.latitude, destination.position.longitude]);
    const onSpotDetail = useCallback(({ spotId, markerType }) => {
        if (!isAdmin && markerType === 'disabled') {
            return;
        }
        onSpotDetailCallback(spotId);
    }, [isAdmin, onSpotDetailCallback]);
    const getZIndex = useZIndex();
    const onPreviewSpot = useCallback(spot => {
        if (spot?.spotId) {
            dispatch(setRecentlyViewed(spot?.spotId));
        }
        setPreviewSpot(spot);
    }, [dispatch]);
    const refreshMap = () => {
        const mapWidget = mapWidgetRef.current;
        const hasSearchRequestIdChanged = mapWidget.setSearchRequestId(searchRequestId);
        const hasFilterIdChanged = prevFilterId !== filterId;
        if (hasFilterIdChanged) {
            setPrevFilterId(filterId);
        }
        mapWidget.setCenterLatLngAndTitle(centerLatLng.lat, centerLatLng.lng, destinationTitle);
        mapWidget.refreshSpots(spots);
        mapWidget.setZoom(zoom);
        // If Spot Details is open then close info window as the user can view details of any spot from the spot list
        // and the info window would not be in sync with the selected spot.
        // Also close info window if the search request ID has changed.
        if (isSpotDetailOpen ||
            hasSearchRequestIdChanged ||
            hasFilterIdChanged) {
            mapWidget.closeInfoWindow();
        }
        // KML layer feature is likely obsolete.
        // Migrated code from the SHGoogleMap.jsx Class component.
        if (kmlMapLayerEnabled) {
            mapWidget.addKMLLayer(kmlMapLayerURL);
        }
    };
    // initialize Google Map Widget
    useEffect(() => {
        if (loaded && !mapWidgetRef.current && mapContainerRef.current) {
            mapWidgetRef.current = googleMapWidget({
                container: mapContainerRef.current,
                googleMapsId,
                zoom,
                centerLatLng,
                centerTitle: destinationTitle,
                kmlMapLayerEnabled,
                kmlMapLayerURL,
                getZIndex,
                onPan: onPanProp,
                onSpotDetail,
                onZoom,
                onGoogleMapLoaded,
                onPreviewSpot,
                showAllSpots,
            });
            refreshMap();
        }
    }, [
        loaded,
        googleMapsId,
        centerLatLng,
        destinationTitle,
        getZIndex,
        kmlMapLayerEnabled,
        kmlMapLayerURL,
        onGoogleMapLoaded,
        onSpotDetail,
        onPanProp,
        onZoom,
        zoom,
        setPreviewSpot,
        showAllSpots,
        onPreviewSpot,
        refreshMap,
    ]);
    // update Google map
    if (mapWidgetRef.current) {
        refreshMap();
    }
    // unmount cleanup
    useEffect(() => {
        return () => {
            mapWidgetRef.current?.cleanup();
        };
    }, []);
    return (<>
            <GoogleMapInfoWindow spot={previewSpot} onClick={onSpotDetail}/>
            <div ref={mapContainerRef} className="SHGoogleMap"/>
        </>);
};
export default SHGoogleMap;
/**
 * @typedef {object} Spot
 * @property {string} spotId - The ID of the spot.
 * @property {string} markerLabelText - The label text of the marker.
 * @property {boolean} recentlyViewed - Whether the spot was recently viewed or not.
 * @property {number} distanceFromDestination - The distance from the destination.
 */
/**
 * @typedef {object} SHGoogleMapProps
 * @property {boolean} isAdmin - Whether the user is an admin or not.
 * @property {object} destination - The destination object containing information about the destination.
 * @property {boolean} kmlMapLayerEnabled - Whether the KML map layer is enabled or not.
 * @property {string} kmlMapLayerURL - The URL of the KML map layer.
 * @property {Spot[]} spots - The spots to be displayed on the map.
 * @property {string} searchRequestId - The ID of the search request.
 * @property {number} zoom - The zoom level of the map.
 * @property {Function} onGoogleMapLoaded - The callback to be called when the Google Map is loaded.
 * @property {Function} onMarkerClick - The callback to be called when spot details are needed.
 * @property {Function} onPan - The callback to be called when the map is panned.
 * @property {Function} onZoom - The callback to be called when the map is zoomed.
 * @param {boolean} options.showAllSpots - The boolean value indicating whether the all spots needs to be shown in the map.
 * @returns {object} The SHGoogleMap component.
 */
